<template>
    <v-container>
        <v-row v-if="country === 'VEN'" justify="center">
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader
                    class="py-2 mx-auto" width="100%"
                    type="list-item-avatar, divider, card-heading, list-item-three-line, list-item-three-line"
                    v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-text class="pb-1">
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-bank</v-icon> Referencia
                        </h3>
                        <v-select
                            v-model="venRateKey"
                            :items="availableRates"
                            item-text="title"
                            item-value="key"
                            :disabled="!availableRates.length"
                            :menu-props="selectMenuProps"
                            color="primary"
                            item-color="primary"
                            filled
                            hide-details
                        ></v-select>
                        <h3 class="px-1">
                            <v-icon>mdi-shuffle-variant</v-icon> Tipo de Conversión
                        </h3>
                        <v-radio-group v-model="venType" mandatory>
                            <v-radio label="Dólares a bolívares" value="to-ves"></v-radio>
                            <v-radio label="Bolívares a dólares" value="from-ves"></v-radio>
                        </v-radio-group>
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-cash-check</v-icon> {{ labels.amount }}
                        </h3>
                        <v-currency-field
                            v-model="venAmount"
                            :disabled="!selectedVenRate"
                            filled
                            :prefix="venType === 'to-ves' ? '$' : 'Bs. '"
                            :suffix="venType === 'to-ves' ? (selectedVenRate ? selectedVenRate.from : '') : 'VES'"
                            v-bind="venType === 'to-ves' ? currency_usd : currency_cop"
                        ></v-currency-field>
                        <v-chip-group class="mt-1" column>
                            <v-chip
                                v-for="preset in venAmountPresets"
                                :key="preset"
                                color="secondary"
                                text-color="primary"
                                @click="setVenAmount(preset)"
                            >
                                {{ venType === 'to-ves' ? '$' : 'Bs. ' }}{{ Number(preset).toLocaleString("en-US") }}
                            </v-chip>
                        </v-chip-group>
                        <v-divider class="my-3"></v-divider>
                        <div class="calc-memory">
                            <div class="d-flex align-center flex-wrap">
                                <v-btn small outlined color="primary" :disabled="!canCalculateVen" @click="saveFavoriteAmount(venAmount)">
                                    <v-icon left small>mdi-star-plus</v-icon>
                                    Guardar monto
                                </v-btn>
                                <v-btn v-if="favoriteAmounts.length" small text color="error" class="ml-1" @click="clearFavoriteAmounts">
                                    Limpiar
                                </v-btn>
                            </div>
                            <v-chip-group v-if="favoriteAmounts.length" class="mt-2" column>
                                <v-chip
                                    v-for="favorite in favoriteAmounts"
                                    :key="'ven-fav-' + favorite"
                                    close
                                    color="secondary"
                                    text-color="primary"
                                    @click="setVenAmount(favorite)"
                                    @click:close="removeFavoriteAmount(favorite)"
                                >
                                    {{ formatActiveAmount(favorite) }}
                                </v-chip>
                            </v-chip-group>
                        </div>
                    </v-card-text>
                    <v-card-actions class="justify-center pt-1 px-4 pb-4">
                        <v-btn block large color="primary" :disabled="!canCalculateVen" @click="copyVenResult">
                            <v-icon left>mdi-content-copy</v-icon>
                            Copiar Resultado
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-text</v-icon> RESULTADO
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-alert v-if="!canCalculateVen" class="ma-4" dense outlined type="warning">
                        No hay una tasa disponible para Venezuela.
                    </v-alert>
                    <template v-else>
                        <v-card-text class="text-center text-h4 font-weight-bold pb-1 bg--text">
                            {{ formattedVenResult }}
                        </v-card-text>
                        <v-card-text class="text-center text-body-1 font-weight-bold py-1">
                            Referencia aplicada
                        </v-card-text>
                        <v-card-text class="text-center font-weight-bold py-1">
                            {{ venRateText }}
                        </v-card-text>
                        <v-card-text class="text-center pt-1">
                            {{ venResultText }}
                        </v-card-text>
                    </template>
                    <v-divider v-if="recentCalculations.length"></v-divider>
                    <v-card-text v-if="recentCalculations.length" class="pt-3">
                        <div class="d-flex align-center mb-2">
                            <div class="text-caption font-weight-bold flex-grow-1">Últimos cálculos</div>
                            <v-btn icon small color="error" @click="clearRecentCalculations">
                                <v-icon small>mdi-delete-sweep</v-icon>
                            </v-btn>
                        </div>
                        <v-list dense class="pa-0 transparent">
                            <v-list-item
                                v-for="recent in recentCalculations"
                                :key="recent.id"
                                class="px-0"
                                @click="applyRecentCalculation(recent)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>{{ recent.result }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ recent.detail }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-else justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="d-block py-2 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-calculator</v-icon> CALCULADORA
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>
        <v-row v-if="country !== 'VEN'" justify="center">
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader
                    class="py-2 mx-auto" width="100%"
                    type="list-item-avatar, divider, card-heading, list-item-three-line, list-item-three-line"
                    v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-text class="pb-1">
                        <div class="d-flex align-center px-1">
                            <h3 class="mb-0 flex-grow-1">
                                <v-icon>mdi-shuffle-variant</v-icon> Tipo de Conversión
                            </h3>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        icon
                                        color="primary"
                                        :disabled="loading"
                                        @click="invertConversion"
                                        v-bind="attrs"
                                        v-on="on"
                                    >
                                        <v-icon>mdi-swap-horizontal</v-icon>
                                    </v-btn>
                                </template>
                                <span>Invertir conversión</span>
                            </v-tooltip>
                        </div>
                        <v-radio-group v-model="type" :disabled="loading" mandatory>
                            <v-radio
                                v-for="i in types"
                                :key="i.text"
                                :label="i.text"
                                :value="i.value"
                            ></v-radio>
                        </v-radio-group>
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-consolidate</v-icon> {{ labels.date }}
                        </h3>
                        <v-menu
                            ref="menu"
                            v-model="menu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="date"
                                    name="date"
                                    prepend-inner-icon="mdi-calendar-cursor"
                                    hint="Ejemplo='año-mes-dia'"
                                    persistent-hint
                                    :error-messages="errors.date"
                                    :rules="[rules.req('date')]"
                                    :disabled="loading"
                                    @input="clearErrors('date')"
                                    readonly
                                    filled
                                    v-bind="attrs"
                                    v-on="on"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="date"
                                :active-picker.sync="activePicker"
                                color="primary"
                                :max="latestPrice ? latestPrice.date : undefined"
                                min="1991-12-02"
                                @change="save"
                            ></v-date-picker>
                        </v-menu>
                        <h3 class="px-1 pb-2">
                            <v-icon>mdi-cash-check</v-icon> {{ labels.amount }}
                        </h3>
                        <v-currency-field
                            v-model="amount"
                            :error-messages="errors.amount"
                            :rules="[rules.required('amount'), rules.minus('amount', amount, 0.01, 'El')]"
                            :disabled="loading"
                            @input="clearErrors('amount')"
                            filled
                            :prefix="type.fromSymbol"
                            :suffix="type.from"
                            v-bind="type.from === 'COP'? currency_cop : currency_usd"
                        ></v-currency-field>
                        <v-chip-group class="mt-1" column>
                            <v-chip
                                v-for="preset in amountPresets"
                                :key="preset"
                                color="secondary"
                                text-color="primary"
                                @click="setAmount(preset)"
                            >
                                {{ type.fromSymbol + Number(preset).toLocaleString("en-US") + ' ' + type.from }}
                            </v-chip>
                        </v-chip-group>
                        <v-divider class="my-3"></v-divider>
                        <div class="calc-memory">
                            <div class="d-flex align-center flex-wrap">
                                <v-btn small outlined color="primary" :disabled="!canCalculate" @click="saveFavoriteAmount(amount)">
                                    <v-icon left small>mdi-star-plus</v-icon>
                                    Guardar monto
                                </v-btn>
                                <v-btn v-if="favoriteAmounts.length" small text color="error" class="ml-1" @click="clearFavoriteAmounts">
                                    Limpiar
                                </v-btn>
                            </div>
                            <v-chip-group v-if="favoriteAmounts.length" class="mt-2" column>
                                <v-chip
                                    v-for="favorite in favoriteAmounts"
                                    :key="'col-fav-' + favorite"
                                    close
                                    color="secondary"
                                    text-color="primary"
                                    @click="setAmount(favorite)"
                                    @click:close="removeFavoriteAmount(favorite)"
                                >
                                    {{ formatActiveAmount(favorite) }}
                                </v-chip>
                            </v-chip-group>
                        </div>
                    </v-card-text>
                    <v-card-actions class="justify-center pt-1 px-4 pb-4">
                        <v-btn block large color="primary" :disabled="!canCalculate" @click="copyResult">
                            <v-icon left>mdi-content-copy</v-icon>
                            Copiar Resultado
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col align-self="stretch" cols="12" md="6">
                <v-skeleton-loader class="my-2 mx-auto" width="100%" type="list-item-avatar, divider, card-heading, list-item-three-line, list-item-three-line" v-if="loading === true"/>
                <v-card height="100%" v-else>
                    <v-card-title class="d-block py-1 text-center font-weight-bold card bg--text" style="word-break: normal">
                        <v-icon color="bg">mdi-text</v-icon> RESULTADO
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-alert v-if="!canCalculate" class="ma-4" dense outlined type="warning">
                        No hay una TRM disponible para la fecha seleccionada.
                    </v-alert>
                    <template v-else>
                        <v-card-text class="text-center text-h4 font-weight-bold pb-1 bg--text">
                            {{ formattedResult }}
                        </v-card-text>
                        <v-card-text class="text-center text-body-1 font-weight-bold py-1">
                            Tasa Aplicada
                        </v-card-text>
                        <v-card-text class="text-center font-weight-bold py-1">
                            {{ formattedRate }}
                        </v-card-text>
                        <v-card-text class="text-center py-1">
                            Esta es la TRM vigente del {{ date | moment("dddd LL") }}
                        </v-card-text>
                        <v-card-text :class="'text-center font-weight-bold py-1 ' + trend.color + '--text'">
                            {{ trend.text }}
                        </v-card-text>
                        <v-card-text class="text-center pt-1">
                            {{ resultText }}
                        </v-card-text>
                    </template>
                    <v-divider v-if="recentCalculations.length"></v-divider>
                    <v-card-text v-if="recentCalculations.length" class="pt-3">
                        <div class="d-flex align-center mb-2">
                            <div class="text-caption font-weight-bold flex-grow-1">Últimos cálculos</div>
                            <v-btn icon small color="error" @click="clearRecentCalculations">
                                <v-icon small>mdi-delete-sweep</v-icon>
                            </v-btn>
                        </div>
                        <v-list dense class="pa-0 transparent">
                            <v-list-item
                                v-for="recent in recentCalculations"
                                :key="recent.id"
                                class="px-0"
                                @click="applyRecentCalculation(recent)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>{{ recent.result }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ recent.detail }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>


<script>
import Form from "../helpers/form";
import { toWords } from "n2words/es";

export default {
    name: "Calculator",
    mixins: [ Form ],
    props: [ 'country', 'items', 'rates', 'load' ],
    data: () => ({
        labels: {
          date: 'Fecha TRM',
          amount: 'Monto a Convertir'
        },
        loading: true,
        prices: [],
        types: [
            {
                text: 'Convertir USD a COP',
                value: {
                    fromSymbol: '$',
                    fromName: 'dólares estadounidenses',
                    from: 'USD',
                    toSymbol: '$',
                    toName: 'pesos colombianos',
                    to: 'COP'
                }
            },
            {
                text: 'Convertir COP a USD',
                value: {
                    fromSymbol: '$',
                    fromName: 'pesos colombianos',
                    from: 'COP',
                    toSymbol: '$',
                    toName: 'dólares estadounidenses',
                    to: 'USD'
                }
            }
        ],
        type: {
            fromSymbol: '$',
            fromName: 'dólares estadounidenses',
            from: 'USD',
            toSymbol: '$',
            toName: 'pesos colombianos',
            to: 'COP'
        },
	        amount: 10,
	        venRateKey: 'digital-usd',
	        venType: 'to-ves',
	        venAmount: 1,
	        activePicker: null,
        date: null,
        menu: false,
        favoriteAmounts: [],
        recentCalculations: [],
        selectMenuProps: {
            bottom: true,
            offsetY: true,
            nudgeBottom: 4,
            maxHeight: 240,
        },
        currency_cop: {
            locale: 'en-US',
            decimalLength: 0,
            autoDecimalMode: false,
            min: null,
            max: null,
            defaultValue: 10000,
            valueAsInteger: false,
            allowNegative: false
        },
        currency_usd: {
            locale: 'en-US',
            decimalLength: 2,
            autoDecimalMode: false,
            min: null,
            max: null,
            defaultValue: 10,
            valueAsInteger: false,
            allowNegative: false
        },
        result: {
            base: 0,
            amount: 0,
            from: 'USD',
            to: 'COP'
        }
    }),
	    computed: {
	        availableRates() {
	            const priority = {
	                'digital-usd': 0,
	                'bcv-usd': 1,
	            };

	            return (this.rates || [])
	                .filter(rate => Number(rate.value) > 0)
	                .sort((a, b) => (priority[a.key] || 9) - (priority[b.key] || 9));
	        },
	        selectedVenRate() {
	            return this.availableRates.find(rate => rate.key === this.venRateKey) || this.availableRates[0] || null;
	        },
	        canCalculateVen() {
	            return !!this.selectedVenRate && Number(this.venAmount) >= 0.01;
	        },
	        venAmountPresets() {
	            if (this.venType === 'from-ves') {
	                return [100, 500, 1000, 5000, 10000, 50000];
	            }

	            return [1, 5, 10, 20, 50, 100];
	        },
	        venResult() {
	            if (!this.canCalculateVen) {
	                return 0;
	            }

	            return this.venType === 'from-ves' ? this.venAmount / this.selectedVenRate.value : this.venAmount * this.selectedVenRate.value;
	        },
	        formattedVenResult() {
	            if (!this.selectedVenRate) {
	                return this.formatMoney(0, 'Bs. ', 'VES', 2);
	            }

	            if (this.venType === 'from-ves') {
	                return this.formatMoney(this.venResult, '$', this.selectedVenRate.from, this.venResult <= 1 ? 4 : 2);
	            }

	            return this.formatMoney(this.venResult, this.selectedVenRate.symbol, this.selectedVenRate.to, 2);
	        },
	        venRateText() {
	            if (!this.selectedVenRate) {
	                return '';
	            }

	            return '$1 ' + this.selectedVenRate.from + ' = ' + this.formatMoney(this.selectedVenRate.value, this.selectedVenRate.symbol, this.selectedVenRate.to, 2);
	        },
	        venResultText() {
	            const fromText = this.venType === 'from-ves'
	                ? this.formatMoney(this.venAmount, 'Bs. ', 'VES', 2)
	                : this.formatMoney(this.venAmount, '$', this.selectedVenRate.from, 2);

	            return fromText + ' equivale a ' + this.formattedVenResult + ' usando ' + this.selectedVenRate.title + '.';
	        },
	        selectedPrice() {
            return this.prices.find(({ date }) => date === this.date);
        },
        latestPrice() {
            return this.prices.length ? this.prices[0] : null;
        },
        canCalculate() {
            return !!this.selectedPrice && Number(this.amount) >= 0.01;
        },
        amountPresets() {
            if (this.type.from === 'COP') {
                return [10000, 20000, 50000, 100000, 200000, 500000];
            }

            return [1, 5, 10, 20, 50, 100, 500, 1000];
        },
        formattedResult() {
            return this.formatMoney(this.result.amount, this.type.toSymbol, this.type.to, this.result.amount <= 1 ? 4 : 2);
        },
        formattedRate() {
            return '$1 USD = ' + this.formatMoney(this.result.base, '$', this.result.to, 2);
        },
        resultText() {
            return this.formatMoney(this.amount, this.type.fromSymbol, this.type.from, this.type.from === 'COP' ? 0 : 2) +
                ' (' + this.toWords(this.amount) + ' ' + this.type.fromName + ') equivale a ' +
                this.formattedResult + ' (' + this.toWords((this.result.amount).toFixed((this.result.amount <= 1 ? 4 : 2))) + ' ' + this.type.toName + ')';
        },
        trend() {
            if (!this.latestPrice || !this.selectedPrice || this.latestPrice.date === this.date) {
                return {
                    color: 'primary',
                    text: 'Esta es la TRM mas reciente disponible.'
                };
            }

            const difference = Number(this.selectedPrice.value) - Number(this.latestPrice.value);
            const symbol = difference >= 0 ? '+' : '';
            const color = difference >= 0 ? 'success' : 'error';

            return {
                color,
                text: 'Diferencia frente a la TRM mas reciente: ' + symbol + this.formatMoney(Math.abs(difference), '$', 'COP', 2)
            };
        },
        memoryKey() {
            if (this.country === 'VEN') {
                return 'dollar.calculator.favorites.VEN.' + this.venType + '.' + this.venRateKey;
            }

            return 'dollar.calculator.favorites.COL.' + this.type.from + '.' + this.type.to;
        },
        recentKey() {
            return 'dollar.calculator.recent.' + this.country;
        }
    },
    mounted() {
        this.getData();
        this.loadCalculatorMemory();
    },
    watch: {
        load() {
            this.loading = this.load;
        },
        items() {
            const items = Array.isArray(this.items) ? this.items : [];
            this.prices = items;
            this.date = items.length ? items[0].date : null;
            this.getResult();
        },
        country() {
            this.getData();
            this.ensureVenRate();
            this.loadCalculatorMemory();
        },
        rates() {
            this.ensureVenRate();
        },
        menu(val) {
            val && setTimeout(() => (this.activePicker = 'YEAR'))
        },
        amount() {
            this.getResult();
        },
        date() {
            this.getResult();
        },
        type: {
            handler() {
                this.getResult();
                this.loadCalculatorMemory();
            },
            deep: true
        },
        venType() {
            this.loadCalculatorMemory();
        },
        venRateKey() {
            this.loadCalculatorMemory();
        }
    },
    methods: {
        toWords,
        getData() {
            if (this.load === false) {
                const items = Array.isArray(this.items) ? this.items : [];
                this.prices = items;
                this.date = items.length ? items[0].date : null;
                this.loading = this.load;
                this.getResult();
            }
        },
        save (date) {
            this.$refs.menu.save(date)
        },
	        setAmount(amount) {
	            this.amount = amount;
	        },
	        setVenAmount(amount) {
	            this.venAmount = amount;
	        },
        loadCalculatorMemory() {
            try {
                this.favoriteAmounts = JSON.parse(localStorage.getItem(this.memoryKey) || '[]');
                this.recentCalculations = JSON.parse(localStorage.getItem(this.recentKey) || '[]');
            } catch (e) {
                this.favoriteAmounts = [];
                this.recentCalculations = [];
            }
        },
        saveFavoriteAmount(amount) {
            const value = Number(amount);

            if (!value || value < 0.01) {
                return;
            }

            const favorites = [value].concat(this.favoriteAmounts.filter(item => Number(item) !== value)).slice(0, 8);
            this.favoriteAmounts = favorites;
            localStorage.setItem(this.memoryKey, JSON.stringify(favorites));
            this.$toast.success('Monto guardado.', { icon: 'mdi-star-check' });
        },
        removeFavoriteAmount(amount) {
            const favorites = this.favoriteAmounts.filter(item => Number(item) !== Number(amount));
            this.favoriteAmounts = favorites;
            localStorage.setItem(this.memoryKey, JSON.stringify(favorites));
        },
        clearFavoriteAmounts() {
            this.favoriteAmounts = [];
            localStorage.removeItem(this.memoryKey);
        },
        formatActiveAmount(amount) {
            if (this.country === 'VEN') {
                return (this.venType === 'to-ves' ? '$' : 'Bs. ') + Number(amount).toLocaleString("en-US");
            }

            return this.type.fromSymbol + Number(amount).toLocaleString("en-US") + ' ' + this.type.from;
        },
        saveRecentCalculation(entry) {
            const recent = [entry]
                .concat(this.recentCalculations.filter(item => item.id !== entry.id))
                .slice(0, 5);

            this.recentCalculations = recent;
            localStorage.setItem(this.recentKey, JSON.stringify(recent));
        },
        clearRecentCalculations() {
            this.recentCalculations = [];
            localStorage.removeItem(this.recentKey);
        },
        applyRecentCalculation(recent) {
            if (recent.country !== this.country) {
                return;
            }

            if (this.country === 'VEN') {
                this.venType = recent.type;
                this.venRateKey = recent.rateKey;
                this.venAmount = recent.amount;
                return;
            }

            const nextType = this.types.find(item => item.value.from === recent.from && item.value.to === recent.to);

            if (nextType) {
                this.type = nextType.value;
            }

            this.amount = recent.amount;
            this.date = recent.date || this.date;
        },
        ensureVenRate() {
            if (this.country !== 'VEN') {
                return;
            }

            const digital = this.availableRates.find(rate => rate.key === 'digital-usd');
            const exists = this.availableRates.some(rate => rate.key === this.venRateKey);

            if (digital && (!exists || this.venRateKey === 'bcv-usd')) {
                this.venRateKey = digital.key;
                return;
            }

            if (!exists && this.availableRates.length) {
                this.venRateKey = this.availableRates[0].key;
            }
        },
        invertConversion() {
            const currentType = this.type.from;
            const convertedAmount = this.canCalculate ? Number(this.result.amount) : this.amount;
            const nextType = this.types.find(({ value }) => value.from !== currentType);

            if (nextType) {
                this.type = nextType.value;
                this.amount = Number(convertedAmount.toFixed(this.type.from === 'COP' ? 0 : 2));
            }
        },
        formatMoney(value, symbol, code, decimals = 2) {
            return symbol + Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            }) + ' ' + code;
        },
	        copyResult() {
            if (!this.canCalculate) {
                return;
            }

            const text = this.resultText + '. ' + this.formattedRate + '.';
            this.saveRecentCalculation({
                id: ['COL', this.type.from, this.type.to, this.date, this.amount].join('-'),
                country: 'COL',
                from: this.type.from,
                to: this.type.to,
                amount: Number(this.amount),
                date: this.date,
                result: this.formattedResult,
                detail: this.formatMoney(this.amount, this.type.fromSymbol, this.type.from, this.type.from === 'COP' ? 0 : 2) + ' | ' + this.formattedRate
            });

            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$toast.success('Resultado copiado.', { icon: 'mdi-check-circle' });
                }).catch(() => {
                    this.copyWithFallback(text);
                });
                return;
            }

	            this.copyWithFallback(text);
	        },
	        copyVenResult() {
	            if (!this.canCalculateVen) {
	                return;
	            }

	            this.saveRecentCalculation({
	                id: ['VEN', this.venType, this.venRateKey, this.venAmount].join('-'),
	                country: 'VEN',
	                type: this.venType,
	                rateKey: this.venRateKey,
	                amount: Number(this.venAmount),
	                result: this.formattedVenResult,
	                detail: this.formatActiveAmount(this.venAmount) + ' | ' + this.selectedVenRate.title
	            });
	            this.copyWithFallback(this.venResultText + ' ' + this.venRateText + '.');
	        },
        copyWithFallback(text) {
            const input = document.createElement('textarea');
            input.value = text;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            this.$toast.success('Resultado copiado.', { icon: 'mdi-check-circle' });
        },
        getResult() {
            if (!this.canCalculate) {
                this.result = {
                    base: 0,
                    amount: 0,
                    from: 'USD',
                    to: 'COP'
                };
                return;
            }

            const data = this.selectedPrice;
            this.result = {
                base: data.value,
                amount: this.type.from === 'COP' ? this.amount / data.value : this.amount * data.value,
                from: 'USD',
                to: data.currency
            };
        }
    }
}
</script>

<style scoped>

</style>
